import { useEffect, useState } from "react";
import { formatMonthDate, getStartEndOfCurrentMonth } from "../../helper";
import { useGetRevenueStoreApi } from "../../hooks";
import { useAuthStore, useCommonStore, useRevenueStore } from "../../store";
import {
  CardItemRevenueStore,
  FooterFilterMobile,
  LoaderCustom,
  ScrollRestoration,
} from "../../components";
import { ScheduleOutlined, DownloadOutlined } from "@ant-design/icons";
import { RangeCalendar } from "@nextui-org/react";
import useDebounce from "../../hooks/common/useDebounce";
import { emptyIcon, searchIcon } from "../../assets";
import { Dropdown, Button } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import path from "../../ultils/path";
import * as XLSX from "xlsx";

const RevenueStorePage = () => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchQueryGroupName, setSearchQueryGroupName] = useState<string>("");
  const [filteredData, setFilteredData] = useState<any>([]);
  const debouceSearchQuery = useDebounce(searchQuery, 300);
  const { revenueStore, isLoading } = useGetRevenueStoreApi();

  const location = useLocation();
  const storeCode = location.state?.storeCode;

  const { setOpenSort } = useCommonStore();
  const {
    setAccountId,
    setEndDate,
    setStartDate,
    startDate,
    endDate,
    setRevenueStore,
    revenueStore: revenueStoreList,
  } = useRevenueStore();

  const { token } = useAuthStore();
  const navigate = useNavigate();

  const exportToExcel = () => {
    if (!filteredData || filteredData.length === 0) {
      return;
    }

    const excelData = filteredData.map((item: any, index: number) => ({
      "STT": index + 1,
      "Group Name": item.groupName || "",
      "Revenue Total": item.revenueTotal ? item.revenueTotal.toLocaleString('en-US') : "0",
      // "Quantity": item.quantity || 0,
      "Net Revenue (TOTAL INCOME CASH)": (+item.revenueTotal - +item.couponPrice - +item.epayMoney - +item.totalF1).toLocaleString('en-US'),
      "Other Revenue (QR + F1)": (+item.epayMoney + +item.totalF1).toLocaleString('en-US'),
      "Coupon Price (APP + ERROR)": item.couponPrice ? item.couponPrice.toLocaleString('en-US') : "0",
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);

    const colWidths = [
      { wch: 5 },   // STT
      { wch: 20 },  // Group Name
      { wch: 15 },  // Revenue Total
      // { wch: 10 },  // Quantity
      { wch: 30 },  // Coupon Price
      { wch: 25 },  // Other Revenue
      { wch: 25 },  // Net Revenue
    ];
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, "Revenue Store Data");

    const startDateStr = startDate ? formatMonthDate(startDate) : "unknown";
    const endDateStr = endDate ? formatMonthDate(endDate) : "unknown";
    const filename = `Revenue_Store_${startDateStr}_to_${endDateStr}.xlsx`;

    XLSX.writeFile(wb, filename);
  };

  useEffect(() => {
    if (!token) {
      navigate(path.SIGN_IN);
    }
  }, []);

  useEffect(() => {
    if (revenueStore?.data && debouceSearchQuery) {
      const filterItem = revenueStore?.data?.filter((item: any) =>
        item?.userGroup
          ?.toLowerCase()
          .includes(debouceSearchQuery?.toLowerCase())
      );

      setFilteredData(filterItem);
    } else {
      setFilteredData(revenueStore?.data);
    }
  }, [debouceSearchQuery, revenueStore?.data]);

  const { end, start } = getStartEndOfCurrentMonth();

  useEffect(() => {
    if (startDate && endDate) {
      setStartDate(startDate);
      setEndDate(endDate);
      setAccountId(null);
    } else {
      setStartDate(start);
      setEndDate(end);
      setAccountId(null);
    }
  }, [start, end]);

  useEffect(() => {
    setRevenueStore(revenueStore?.data);
  }, [startDate, endDate, revenueStore]);

  useEffect(() => {
    if (storeCode) {
      setSearchQuery(storeCode);
    }
  }, [storeCode]);

  useEffect(() => {
    // console.log("RevenueStorePage: Current sessionStorage content:");
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key) {
        const value = sessionStorage.getItem(key);
        // console.log(`RevenueStorePage: ${key} = ${value}`);
      }
    }
    return () => {
      const items = document.querySelectorAll('[data-item-index]');
      // console.log("RevenueStorePage: Elements with data-item-index before unmounting:", items.length);
      items.forEach((item, index) => {
        // console.log(`RevenueStorePage: Item ${index}:`, item);
      });
    };
  }, []);

  return (
    <>
      <ScrollRestoration isLoading={isLoading} />
      {isLoading ? (
        <LoaderCustom></LoaderCustom>
      ) : (
        <>
          <div className="w-[100%] items-center md:pb-2 font-semibold text-ok-travel-green flex lg:pb-0 justify-between mb-2 border-b-[1.5px] md:mb-4">
            <h3 className="uppercase max-md:mt-4 max-sm:py-2 px-[16px] md:px-[0px] text-medium text-gray-500 md:text-md xl:text-xl">
              Manage Revenue Store
            </h3>
            {/* Search and Export - desktop */}
            <div className="gap-4 lg:flex hidden">
              <div className="2xl:w-[20vw] xl:h-[4.4vh] md:h-[4vh] h-[3.8vh] font-normal text-gray-500 rounded-sm px-2 xl:w-[18vw] mb-2 lg:w-[16vw] flex items-center gap-2 border-1 border-gray-400 bg-white">
                <img
                  className="2xl:w-6 xl:w-5 w-4 max-md:w-3"
                  alt="search"
                  src={searchIcon}
                ></img>
                <input
                  className=" placeholder:text-sm placeholder:font-normal outline-none bg-transparent"
                  type="text"
                  placeholder="Search Store"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Desktop */}
          <div className="flex max-md:hidden w-full md:flex md:flex-row md:justify-between md:items-center px-4">
            <div className="w-[100%] md:mb-4 flex items-center gap-3">
              <Dropdown
                overlay={
                  <RangeCalendar
                    onChange={(value) => {
                      setStartDate(
                        `${value.start.year}-${
                          value.start.month < 10
                            ? `0${value.start.month}`
                            : value.start.month
                        }-${
                          value.start.day < 10
                            ? `0${value.start.day}`
                            : value.start.day
                        }`
                      );

                      setEndDate(
                        `${value.end.year}-${
                          value.end.month < 10
                            ? `0${value.end.month}`
                            : value.end.month
                        }-${
                          value.end.day < 10
                            ? `0${value.end.day}`
                            : value.end.day
                        }`
                      );
                    }}
                    aria-label="Date (No Selection)"
                  />
                }
                trigger={["click"]}
              >
                <div
                  // onClick={() => setIsShowCalender(!isShowCalender)}
                  className="flex max-sm:text-[12px] text-sm text-gray-400 gap-2 my-4 cursor-pointer justify-between lg:px-4 px-2 py-1 border-[1px] border-gray-300 rounded-full bg-white"
                >
                  <span className="text-sm placeholder:text-sm">
                    {startDate && endDate
                      ? `from ${formatMonthDate(
                          startDate
                        )} to ${formatMonthDate(endDate)}`
                      : "Filter date"}
                  </span>
                  <ScheduleOutlined />
                </div>
              </Dropdown>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={exportToExcel}
                disabled={!filteredData || filteredData.length === 0}
                className="h-[4.4vh] md:h-[4vh] h-[3.8vh] flex items-center justify-center bg-pink-500 hover:bg-pink-600 border-pink-500 text-white"
              >
                Export Excel
              </Button>
            </div>
          </div>

          {/* Mobile */}
          <div className="flex items-center md:hidden w-full px-4 gap-2">
            <div className="w-1/2">
              <Dropdown
                overlay={
                  <RangeCalendar
                    onChange={(value) => {
                      setStartDate(
                        `${value.start.year}-${
                          value.start.month < 10
                            ? `0${value.start.month}`
                            : value.start.month
                        }-${
                          value.start.day < 10
                            ? `0${value.start.day}`
                            : value.start.day
                        }`
                      );

                      setEndDate(
                        `${value.end.year}-${
                          value.end.month < 10
                            ? `0${value.end.month}`
                            : value.end.month
                        }-${
                          value.end.day < 10
                            ? `0${value.end.day}`
                            : value.end.day
                        }`
                      );
                    }}
                    aria-label="Date (No Selection)"
                  />
                }
                trigger={["click"]}
              >
                <div
                  // onClick={() => setIsShowCalender(!isShowCalender)}
                  className="flex max-sm:text-[12px] text-sm text-gray-400 gap-2 my-4 cursor-pointer justify-between lg:px-4 px-2 py-1 border-[1px] border-gray-300 rounded-full bg-white"
                >
                  <span className="text-xs">
                    {startDate && endDate
                      ? `from ${formatMonthDate(
                          startDate
                        )} to ${formatMonthDate(endDate)}`
                      : "Filter date"}
                  </span>
                </div>
              </Dropdown>
            </div>
            <div className="w-1/2 border-[1px] border-gray-300 sm:px-[12px] xl:px-[18px] px-[8px] bg-white rounded-full">
              <input
                className=" text-xs outline-none bg-transparent"
                type="text"
                placeholder="Search Store"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-col gap-3 w-full px-4">
            {revenueStoreList &&
              filteredData &&
              filteredData?.map((item: any, index: number) => {
                return (
                  <div key={index} data-item-index={index}>
                    <CardItemRevenueStore
                      idx={index}
                      data={item}
                    ></CardItemRevenueStore>
                  </div>
                );
              })}
            {revenueStoreList?.length < 1 && (
              <div className="w-full h-full flex-col gap-2 flex max-lg:mt-20 justify-center items-center">
                <img
                  className="max-md:w-[12rem]"
                  alt="empty"
                  src={emptyIcon}
                ></img>
                <span className="text-gray-500">Select a store</span>
              </div>
            )}
          </div>

          <FooterFilterMobile isFilter={true}>
            {/* Mobile */}
            <div className="2xl:w-[20vw] w-full xl:h-[4.4vh] md:h-[4vh] h-[3.6vh] max-sm:h-[5vh] max-sm:rounded-md font-normal text-gray-500 rounded-sm px-2 xl:w-[18vw] mb-2 lg:w-[16vw] flex items-center gap-2 border-1 border-gray-400 bg-white">
              <img
                className="2xl:w-6 xl:w-5 w-4 max-md:w-3"
                src={searchIcon}
                alt="search"
              ></img>
              <input
                className=" placeholder:text-sm placeholder:font-normal outline-none bg-transparent"
                type="text"
                placeholder="Search Store"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={exportToExcel}
              disabled={!filteredData || filteredData.length === 0}
              className="w-full h-[3.6vh] max-sm:h-[5vh] flex items-center justify-center bg-pink-500 hover:bg-pink-600 border-pink-500 text-white"
            >
              Export Excel
            </Button>
            <div className="2xl:w-[20vw] w-full xl:h-[4.4vh] md:h-[4vh] h-[3.6vh]  max-sm:h-[5vh] max-sm:rounded-md font-normal text-gray-500 rounded-sm px-2 xl:w-[18vw] mb-2 lg:w-[16vw] flex items-center gap-2 border-1 border-gray-400 bg-white">
              <img
                className="2xl:w-6 xl:w-5 w-4 max-md:w-3"
                src={searchIcon}
                alt="search"
              ></img>
              <input
                className=" placeholder:text-sm placeholder:font-normal outline-none bg-transparent"
                type="text"
                placeholder="Search Name"
                value={searchQueryGroupName}
                onChange={(e) => setSearchQueryGroupName(e.target.value)}
              />
            </div>
            <div className="sm:w-[50%] md:hidden max-md:w-[100%]">
              <Dropdown
                overlay={
                  <RangeCalendar
                    className="fixed w-auto"
                    onChange={(value) => {
                      setStartDate(
                        `${value.start.year}-${
                          value.start.month < 10
                            ? `0${value.start.month}`
                            : value.start.month
                        }-${
                          value.start.day < 10
                            ? `0${value.start.day}`
                            : value.start.day
                        }`
                      );

                      setEndDate(
                        `${value.end.year}-${
                          value.end.month < 10
                            ? `0${value.end.month}`
                            : value.end.month
                        }-${
                          value.end.day < 10
                            ? `0${value.end.day}`
                            : value.end.day
                        }`
                      );
                      // setIsShowCalender(false);
                      setOpenSort(false);
                    }}
                    aria-label="Date (No Selection)"
                  />
                }
                trigger={["click"]}
              >
                <div className="2xl:w-[20vw] w-full xl:h-[4.4vh] md:h-[4vh] h-[3.6vh]  max-sm:h-[5vh] max-sm:rounded-md font-normal text-gray-500 rounded-sm px-2 xl:w-[18vw] mb-2 lg:w-[16vw] flex items-center gap-2 border-1 border-gray-400 bg-white">
                  <ScheduleOutlined className="2xl:w-6 xl:w-5 w-4 max-md:w-3" />
                  <span className="text-[12px] text-gray-500 font-medium">
                    {startDate && endDate
                      ? `From ${formatMonthDate(
                          startDate
                        )} to ${formatMonthDate(endDate)}`
                      : "Filter date"}
                  </span>
                </div>
              </Dropdown>
            </div>
          </FooterFilterMobile>
        </>
      )}
    </>
  );
};

export default RevenueStorePage;
